import { Box, CollapsableCard, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import type { PayrollCompensationQuery } from 'generated/resources/graphql'
import { TogglePayrollCompensationDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type AttendancePayrollCompensation =
  PayrollCompensationQuery['attendance']['payrollCompensationsConnection']['nodes'][number]

interface Props {
  settingId: number
  payrollCompensation: AttendancePayrollCompensation | undefined
}

const PayrollCompensation = ({ settingId, payrollCompensation }: Props) => {
  const [togglePayrollCompensation] = useMutation(TogglePayrollCompensationDocument, {
    extractorFn: (data) => data.attendanceMutations.toggleAttendancePayrollCompensation,
    refetchQueries: ['AttendancePayrollCompensation'],
  })

  const compensationEnabled = payrollCompensation?.compensationEnabled ?? false

  const form = useForm({
    defaultValues: {
      attendanceEmployeesSettingId: settingId,
      compensationEnabled: compensationEnabled,
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await togglePayrollCompensation({
        variables: {
          attendanceEmployeesSettingId: data.attendanceEmployeesSettingId,
          compensationEnabled: data.compensationEnabled,
        },
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      }

      return handled
    },
  })

  const title = i18n.t('time_tracking.settings.overtime.compensation.payroll.card.title')
  const description = i18n.t(
    'time_tracking.settings.overtime.compensation.payroll.card.description'
  )

  return (
    <CollapsableCard
      open={false}
      Card={
        <Box flexDirection="row" justifyContent="spaceBetween">
          <Box gap="s8">
            <Text.Normal weight="medium">{title}</Text.Normal>
            <Text.Normal>{description}</Text.Normal>
          </Box>
          <Box>
            <TogglerCheckbox
              value={compensationEnabled}
              onChange={(newValue) =>
                form
                  .setValues({
                    ...form.values,
                    compensationEnabled: newValue,
                  })
                  .then(() => {
                    form.handleSubmit()
                  })
              }
              labelBefore
            />
          </Box>
        </Box>
      }
      Content={<></>}
    />
  )
}

export default observer(PayrollCompensation)
